import { useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';

import { useRequest } from '@frontend/api';

import { MFG_QUERY_KEYS } from './constants';
import type {
  AdskMfgQueryResource,
  FusionFolder,
  AdskMfgParams,
  AdskMfgQueryResponse,
  AdskMfgInfiniteQueryFunction,
} from './types';
import { adskRequest, isAdskMfgError } from './utils';

const GRAPHQL_QUERY = {
  operationName: 'GetFoldersByProject',
  query: `query GetFoldersByProject($projectId:ID!, $pagination: PaginationInput) {
        project(projectId: $projectId) {
            folders(pagination: $pagination) {
                pagination {
                    cursor
                }
                results {
                    name
                    id
                }
            }
        }
    }`,
};

type DataType = { folders: AdskMfgQueryResource<FusionFolder[]> };
type ReturnType = {
  project: DataType;
};

type Params = AdskMfgParams & {
  projectId: string | null;
};

type ListFoldersByProjectQueryKey = readonly [
  typeof MFG_QUERY_KEYS.LIST_FOLDERS_BY_PROJECT,
  string,
];

const getListFoldersByProjectQueryKey = (
  projectId: string,
): ListFoldersByProjectQueryKey => [
  MFG_QUERY_KEYS.LIST_FOLDERS_BY_PROJECT,
  projectId,
];

export const useListFoldersByProject: AdskMfgInfiniteQueryFunction<
  AdskMfgQueryResponse<ReturnType>,
  [Params],
  ListFoldersByProjectQueryKey,
  FusionFolder
> = (
  { accessToken, refreshAccess, projectId, pagination = { cursor: null } },
  { queryOptions } = {},
) => {
  const queryClient = useQueryClient();
  const request = useRequest();
  const results = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- adskRequest should be built with a hook, pagination should be added to the query key
    queryKey: getListFoldersByProjectQueryKey(projectId ?? ''),
    queryFn: ({ signal, pageParam }) =>
      adskRequest<DataType, 'project'>(
        accessToken ?? '',
        {
          ...GRAPHQL_QUERY,
          variables: {
            projectId,
            pagination: { ...pagination, cursor: pageParam },
          },
        },
        request,
        { init: { signal } },
        (data) => !!data.project && !!data.project.folders,
        (msg) => `Failed to list folders for project ${projectId}: ${msg}`,
      ),
    enabled: !!accessToken && !!projectId,
    getNextPageParam: (currentPage) =>
      currentPage.data.project.folders.pagination.cursor === null
        ? null
        : currentPage.data.project.folders.pagination.cursor,
    ...queryOptions,
    onError: async (error) => {
      // If not an ADSK error, bail
      if (!isAdskMfgError(error)) return;

      // Trigger refresh on 401s
      if (error.statusCode === 401) {
        await refreshAccess();

        // Invalidate query to trigger a new request
        queryClient.invalidateQueries(
          getListFoldersByProjectQueryKey(projectId ?? ''),
        );
        return;
      }

      // Run the passed in onError if provided
      queryOptions?.onError?.(error);
    },
  });

  const flatData = useMemo(
    () =>
      results?.data?.pages?.flatMap(
        (page) => page.data.project.folders.results,
      ),
    [results?.data?.pages],
  );

  const flatErrors = useMemo(
    () => results?.data?.pages?.flatMap((page) => page?.errors ?? []),
    [results?.data?.pages],
  );

  return { ...results, flatData, flatErrors };
};
